import { useEffect, useRef } from "react";
import { toast } from "sonner";

const HEALTH_URL = "/api/health";
const INTERVAL_MS = 10_000; // verifica a cada 10 segundos
const TIMEOUT_MS = 5_000;   // timeout da requisição
const TOAST_ID = "server-down";

/** Retorna true se a URL atual for a landing page (sem subdomínio de tenant) */
function isLandingPageHost(): boolean {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return !new URLSearchParams(window.location.search).get("tenant");
  }
  const parts = hostname.split(".");
  // www ou apex domain = landing page
  if (parts.length <= 3) return true;
  if (parts[0] === "www") return true;
  return false;
}

export function useServerHealth() {
  const isDownRef = useRef(false);

  useEffect(() => {
    // Não monitorar servidor na landing page
    if (isLandingPageHost()) return;
    async function check() {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

        const res = await fetch(HEALTH_URL, { signal: controller.signal });
        clearTimeout(timer);

        // Considera online se conseguiu resposta (qualquer status 2xx ou 5xx do próprio servidor)
        // Só lança erro se for falha de rede/timeout (catch abaixo)
        if (res.status < 600) {
          toast.dismiss(TOAST_ID);
          if (isDownRef.current) {
            toast.success("Servidor reconectado", { id: "server-up", duration: 4000 });
            isDownRef.current = false;
          }
        }
      } catch {
        if (!isDownRef.current) {
          isDownRef.current = true;
          // Toast fixo enquanto o servidor estiver fora
          toast.error("⚠️ Servidor offline — tentando reconectar…", {
            id: TOAST_ID,
            duration: Infinity,
            dismissible: false,
          });
        }
      }
    }

    // Verifica imediatamente ao montar
    check();

    const interval = setInterval(check, INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);
}
